import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/lib/auth";
import { MaintenanceProvider, useMaintenance } from "@/lib/maintenance";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import PermissionRoute from "@/components/PermissionRoute";
import ScrollToTopButton from "@/components/ScrollToTopButton";
import Home from "@/pages/Home";
import MenuPage from "@/pages/Menu";
import Gallery from "@/pages/Gallery";
import Reviews from "@/pages/Reviews";
import Reservation from "@/pages/Reservation";
import Legal from "@/pages/Legal";
import Order from "@/pages/Order";
import OrderSuccess from "@/pages/OrderSuccess";
import OrdersDisabled from "@/pages/OrdersDisabled";
import MaintenancePage from "@/pages/MaintenancePage";
import Install from "@/pages/Install";
import AdminLogin from "@/pages/admin/AdminLogin";
import Dashboard from "@/pages/admin/Dashboard";
import MenuManagement from "@/pages/admin/MenuManagement";
import HoursManagement from "@/pages/admin/HoursManagement";
import PhotoManagement from "@/pages/admin/PhotoManagement";
import HomepageManagement from "@/pages/admin/HomepageManagement";
import ReservationManagement from "@/pages/admin/ReservationManagement";
import OrderManagement from "@/pages/admin/OrderManagement";
import Settings from "@/pages/admin/Settings";
import ActusManagement from "@/pages/admin/ActusManagement";
import UsersManagement from "@/pages/admin/UsersManagement";
import ResetPassword from "@/pages/admin/ResetPassword";
import ReviewsManagement from "@/pages/admin/ReviewsManagement";

import PlatDuJourAdmin from "@/pages/admin/PlatDuJourAdmin";
import OcrMenuPage from "@/pages/admin/OcrMenuPage";
// Remonter en haut de page à chaque navigation
function useScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
}

function PublicRoute({ children }) {
  const { isMaintenanceMode, loading } = useMaintenance();
  if (loading) return null;
  if (isMaintenanceMode) return <MaintenancePage />;
  return children;
}

import { Link } from "react-router-dom";

function NotFoundPage() {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center px-6" data-testid="not-found-page">
      <div className="text-center">
        <h1 className="text-7xl font-heading font-bold text-garden-800 mb-4">404</h1>
        <p className="text-lg text-charcoal/70 mb-8">Cette page n'existe pas ou a été déplacée.</p>
        <Link to="/" className="inline-flex items-center gap-2 bg-garden-800 text-white px-6 py-3 rounded-full hover:bg-garden-900 transition-colors font-medium" data-testid="back-home-btn">
          Retour à l'accueil
        </Link>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  useScrollToTop();
  useVersionCheck(); // Déclencher la surveillance de version silencieuse en arrière-plan
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
      <Route path="/carte" element={<PublicRoute><MenuPage /></PublicRoute>} />
      <Route path="/galerie" element={<PublicRoute><Gallery /></PublicRoute>} />
      <Route path="/avis" element={<PublicRoute><Reviews /></PublicRoute>} />
      <Route path="/reservation" element={<PublicRoute><Reservation /></PublicRoute>} />
      <Route path="/commander" element={<PublicRoute><Order /></PublicRoute>} />
      <Route path="/commande/succes" element={<PublicRoute><OrderSuccess /></PublicRoute>} />
      <Route path="/commandes-indisponibles" element={<PublicRoute><OrdersDisabled /></PublicRoute>} />
      <Route path="/mentions-legales" element={<PublicRoute><Legal /></PublicRoute>} />
      <Route path="/install" element={<Install />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin/reset-password" element={<ResetPassword />} />
      <Route path="/admin" element={<PermissionRoute module="DASHBOARD"><Dashboard /></PermissionRoute>} />
      <Route path="/admin/menu" element={<PermissionRoute module="MENU"><MenuManagement /></PermissionRoute>} />
      <Route path="/admin/plat-du-jour" element={<PermissionRoute module="MENU"><PlatDuJourAdmin /></PermissionRoute>} />
      <Route path="/admin/ocr-menu" element={<PermissionRoute module="MENU"><OcrMenuPage /></PermissionRoute>} />
      <Route path="/admin/horaires" element={<PermissionRoute module="HOURS"><HoursManagement /></PermissionRoute>} />
      <Route path="/admin/photos" element={<PermissionRoute module="PHOTOS"><PhotoManagement /></PermissionRoute>} />
      <Route path="/admin/homepage" element={<PermissionRoute module="HOMEPAGE_SECTIONS"><HomepageManagement /></PermissionRoute>} />
      <Route path="/admin/reservations" element={<PermissionRoute module="RESERVATIONS"><ReservationManagement /></PermissionRoute>} />
      <Route path="/admin/commandes" element={<PermissionRoute module="ORDERS"><OrderManagement /></PermissionRoute>} />
      <Route path="/admin/actus" element={<PermissionRoute module="ACTUS"><ActusManagement /></PermissionRoute>} />
      <Route path="/admin/reviews" element={<PermissionRoute module="REVIEWS"><ReviewsManagement /></PermissionRoute>} />
      <Route path="/admin/utilisateurs" element={<PermissionRoute module="ADMIN_USERS" adminOnly><UsersManagement /></PermissionRoute>} />
      <Route path="/admin/parametres" element={<PermissionRoute module="SETTINGS_CONTENT"><Settings /></PermissionRoute>} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

import { useVersionCheck } from '@/lib/useVersionCheck';

import { LanguageProvider } from "@/lib/i18n";
import HreflangTags from "@/components/HreflangTags";

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <HreflangTags />
        <AuthProvider>
          <MaintenanceProvider>
            <AppRouter />
            <ScrollToTopButton />
            <Toaster position="bottom-right" richColors />
          </MaintenanceProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
